import { useState } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Chip,
  Divider,
  Typography,
  useBreakpoint,
} from "@avenue-8/sidekick-ui";
import type { ContactBriefingHeader as ContactBriefingHeaderType } from "../../contacts.types";
import CheckMarkIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import {
  getContactQueryKey,
  getContactWithPendingChangesetQueryKey,
  useUpdateContact,
  type ContactDetailResponseDto,
} from "@shared/generated/crm";
import { useContactPendingSuggestions } from "@features/Contacts/useContactPendingSuggestions";
import { formatBriefingSource } from "@features/Contacts/signal-mappers";
import { BulletSeparator } from "@shared/components/BulletSeparator";
import { useHasModule } from "@shared/access-control/useHasModule";
import { sidekickModuleEnum } from "@shared/access-control/sidekick-modules";
import { RadarMonitoringHeaderToggle } from "@features/Contacts/components/RadarMonitoringHeaderToggle";
import { useContactDetailMonitoringToggle } from "@features/Contacts/hooks/useContactMonitoringToggle";
import { ContactSummaryChips } from "./ContactSummaryChips";
import { ContactFieldsForm } from "./ContactFieldsForm";

type ContactBriefingHeaderProps = {
  header: ContactBriefingHeaderType;
  contact: ContactDetailResponseDto;
};

export const ContactBriefingHeader = (props: ContactBriefingHeaderProps) => {
  const { header, contact } = props;
  const [showFields, setShowFields] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [useSuggestedValues, setUseSuggestedValues] = useState(false);
  const [formPendingSuggestionsCount, setFormPendingSuggestionsCount] =
    useState<number | null>(null);
  const {
    contactFields,
    pendingSuggestionsCount: pendingSuggestionsCountFromCurrentValues,
    latestPendingFieldSignalAt,
  } = useContactPendingSuggestions(contact);

  const hasRadarModule = useHasModule(sidekickModuleEnum.RADAR);
  const isMonitored = contact.isMonitoredSince !== null;
  const isMobile = useBreakpoint("mobile");

  const queryClient = useQueryClient();

  const {
    toggleMonitoring: handleToggleMonitoring,
    isPending: isTogglePending,
  } = useContactDetailMonitoringToggle({
    contactId: contact.id,
    contactName: header.name,
  });

  const {
    mutate: updateContact,
    isPending: isSavingContact,
    isError: isSavingContactError,
    reset: resetUpdateContactMutation,
  } = useUpdateContact({
    mutation: {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: getContactWithPendingChangesetQueryKey(contact.id),
        });
        queryClient.refetchQueries({
          queryKey: getContactQueryKey(contact.id),
        });
        handleCancelEditing();
      },
    },
  });

  const handleShowContactFields = () => {
    if (isSavingContact) return;
    const nextShowFields = !showFields;
    setShowFields(nextShowFields);
    if (!nextShowFields) {
      setIsEditing(false);
      setUseSuggestedValues(false);
      resetUpdateContactMutation();
    }
  };

  const handleShowSuggestions = () => {
    setShowFields(true);
    setUseSuggestedValues(true);
    setIsEditing(true);
  };

  const handleReviewSuggestions = () => {
    setUseSuggestedValues(true);
    setIsEditing(true);
  };

  const handleStartEditing = () => {
    setUseSuggestedValues(false);
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    setUseSuggestedValues(false);
    setIsEditing(false);
    resetUpdateContactMutation();
  };

  const pendingSuggestionsCount =
    formPendingSuggestionsCount ?? pendingSuggestionsCountFromCurrentValues;
  const hasPendingSuggestions =
    pendingSuggestionsCount > 0 && !useSuggestedValues;
  const isReviewPrompt = !showFields && hasPendingSuggestions;

  const fieldsButtonLabel = isReviewPrompt
    ? `Review ${pendingSuggestionsCount} updates`
    : showFields
      ? "Hide fields"
      : "Show fields";

  const chevronIcon = showFields ? (
    <ExpandLessIcon fontSize="small" />
  ) : (
    <ExpandMoreIcon fontSize="small" />
  );

  const sourceLabels = (contact.sources ?? []).map((source) =>
    formatBriefingSource({ source, sources: contact.sources ?? [] }),
  );

  return (
    <HeaderCard>
      <HeaderRow>
        <ContactBriefingInfo>
          <ContactName variant="h6">{header.name}</ContactName>
          {(header.titleAndCompany || header.location) && (
            <TitleLocationRow>
              {header.titleAndCompany && (
                <MetaText>{header.titleAndCompany}</MetaText>
              )}
              {header.titleAndCompany && header.location && !isMobile && (
                <BulletSeparator />
              )}
              {header.location && <MetaText>{header.location}</MetaText>}
            </TitleLocationRow>
          )}
          {header.crmStatus && (
            <CrmStatusRow>
              <CrmStatusMeta>
                <CheckIcon aria-hidden>
                  <CheckMarkIcon fontSize="small" />
                </CheckIcon>
                <MetaText color="text.secondary">{header.crmStatus}</MetaText>
              </CrmStatusMeta>
              {sourceLabels.length > 0 && (
                <>
                  {!isMobile && <BulletSeparator />}
                  <CrmStatusChips>
                    {sourceLabels.map((label, index) => (
                      <SourceChip
                        key={`${label}-${index}`}
                        label={label}
                        size="small"
                      />
                    ))}
                  </CrmStatusChips>
                </>
              )}
            </CrmStatusRow>
          )}
        </ContactBriefingInfo>
        <HeaderRadarSlot>
          {hasRadarModule && (
            <RadarMonitoringHeaderToggle
              isMonitored={isMonitored}
              isMonitoredSince={contact.isMonitoredSince}
              isTogglePending={isTogglePending}
              onToggle={handleToggleMonitoring}
            />
          )}
        </HeaderRadarSlot>
      </HeaderRow>
      <Divider />
      <SummaryRow>
        <ContactSummaryChips contact={contact} />
        <FieldsActionButton
          color="secondary"
          size="small"
          variant="outlined"
          disabled={isSavingContact}
          onClick={
            isReviewPrompt ? handleShowSuggestions : handleShowContactFields
          }
          startIcon={
            isReviewPrompt ? <AutoFixHighIcon fontSize="small" /> : undefined
          }
          endIcon={chevronIcon}
          data-highlighted={isReviewPrompt ? "true" : undefined}
        >
          {fieldsButtonLabel}
        </FieldsActionButton>
      </SummaryRow>
      <ContactDetails $contactFieldsShown={showFields}>
        <ContactDetailsContent>
          <ContactFieldsForm
            contactId={contact.id}
            latestPendingFieldSignalAt={latestPendingFieldSignalAt}
            contactFields={contactFields}
            isEditing={isEditing}
            useSuggestedValues={useSuggestedValues}
            updateContact={updateContact}
            isSavingContact={isSavingContact}
            isSavingContactError={isSavingContactError}
            onReviewSuggestions={handleReviewSuggestions}
            onStartEditing={handleStartEditing}
            onCancelEditing={handleCancelEditing}
            onPendingSuggestionsCountChange={setFormPendingSuggestionsCount}
          />
        </ContactDetailsContent>
      </ContactDetails>
    </HeaderCard>
  );
};

const HeaderCard = styled.div`
  width: 100%;
  background-color: ${(p) => p.theme.colors.background};
  border-radius: 0.5rem;
  border: solid 1px ${(p) => p.theme.colors.border2};
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  ${(p) => p.theme.media.laptopDown} {
    text-align: center;
  }

  ${(p) => p.theme.media.mobileDown} {
    text-align: left;
  }
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;

  ${(p) => p.theme.media.laptopDown} {
    grid-template-columns: 1fr;
  }
`;

const ContactBriefingInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ContactName = styled(Typography)`
  color: ${(p) => p.theme.text.colors.brand};
`;

const MetaText = styled(Typography)`
  font-size: ${(p) => p.theme.text.fontSizes.m};
  ${(p) => p.theme.media.laptopDown} {
    font-size: ${(p) => p.theme.text.fontSizes.s};
  }
`;

const TitleLocationRow = styled.div`
  display: flex;
  align-items: center;

  ${(p) => p.theme.media.laptopDown} {
    justify-content: center;
  }

  ${(p) => p.theme.media.mobileDown} {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
  }
`;

const CrmStatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.25rem;

  ${(p) => p.theme.media.laptopDown} {
    justify-content: center;
  }

  ${(p) => p.theme.media.mobileDown} {
    flex-wrap: wrap;
    justify-content: flex-start;
    row-gap: 0.5rem;
  }
`;

const CrmStatusMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  ${(p) => p.theme.media.mobileDown} {
    flex: 1 1 100%;
  }
`;

const CrmStatusChips = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
`;

const SourceChip = styled(Chip)`
  background-color: ${(p) => p.theme.colors.background5};

  .MuiChip-label {
    font-size: ${(p) => p.theme.text.fontSizes.s};
    color: ${(p) => p.theme.text.colors.brand};
  }
`;

const CheckIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.theme.colors.success};
  font-size: 1rem;
`;

const HeaderRadarSlot = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;

  ${(p) => p.theme.media.laptopDown} {
    justify-content: center;
  }

  ${(p) => p.theme.media.mobileDown} {
    justify-content: flex-start;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
`;

const FieldsActionButton = styled(Button)`
  flex-shrink: 0;
  white-space: nowrap;
  padding: 0.25rem 1rem;
  min-width: 0;
  font-size: ${(p) => p.theme.text.fontSizes.s};

  &[data-highlighted="true"] {
    border-style: dashed;
    border-color: ${(p) => p.theme.text.colors.brand};
    color: ${(p) => p.theme.text.colors.brand};

    &:hover {
      border-style: dashed;
      border-color: ${(p) => p.theme.text.colors.brand};
    }
  }
`;

const ContactDetails = styled.div<{ $contactFieldsShown?: boolean }>`
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  padding-top: 0;
  transition:
    grid-template-rows 0.3s ease,
    opacity 0.2s ease,
    padding-top 0.3s ease;
  ${(p) =>
    p.$contactFieldsShown &&
    css`
      grid-template-rows: 1fr;
      opacity: 1;
      padding-top: 1rem;
    `}
`;

const ContactDetailsContent = styled.div`
  overflow: hidden;
`;
